import { useRef } from 'react';
import { PageTransition } from '@/shared/ui/animations';
import { useNavigate } from 'react-router';
import type { FolderResponseDto } from '@inference.engine/workspace-api-client/web';
import { FolderPlusIcon, UploadIcon } from 'lucide-react';
import { PROJECT_PERMISSIONS } from '@inference.engine/permissions/web';
import { useHasProjectPermission } from '@/entities/user';
import { ActionButton } from '@/shared/ui';
import { MAX_FOLDER_DEPTH } from '../../folders.constants';
import { useFolderRouteAfterDelete } from '../../hooks/useFolderRouteAfterDelete';
import { AssetsSection } from '../AssetsSection';
import { EmptyFolderPage } from '../EmptyFolderPage';
import { FolderPaneHeader } from '../FolderPaneHeader';
import { SubfolderGrid } from '../SubfolderGrid';
import { useTranslation } from 'react-i18next';

interface FolderPaneProps {
  projectId: string;
  folder: FolderResponseDto;
  onCreateSubfolder: (parentId: string) => void;
}

/**
 * One folder's header and contents, filling the pane beside the rail.
 *
 * A folder holds subfolders or assets, never both, so the body is one of three
 * things: the asset grid, the folders below it, or — while it is still empty —
 * the choice between the two, offered from the header.
 */
export function FolderPane(props: FolderPaneProps) {
  const { t } = useTranslation('projects');

  const { folder, projectId } = props;
  const navigate = useNavigate();

  // Deleting the open folder has to land somewhere, and the folder it lands on
  // is read while the deleted one is still in the rail. `replace` keeps a page
  // for a folder that no longer exists out of the back stack.
  const routeAfterDelete = useFolderRouteAfterDelete(projectId, folder);

  const uploadInputRef = useRef<HTMLInputElement>(null);
  const canUpload = useHasProjectPermission([PROJECT_PERMISSIONS.ProjectUploadAssets]);

  // Subfolders decide first, and the order is load-bearing: `fileCount` is a
  // subtree rollup, so a folder of folders reports every asset nested beneath
  // it. Testing it first would show the assets of the whole branch in place of
  // the folders that hold them.
  const hasSubfolders = folder.foldersCount > 0;
  const hasFiles = folder.fileCount > 0;
  const isEmpty = !hasSubfolders && !hasFiles;
  const canNestDeeper = folder.depth < MAX_FOLDER_DEPTH;
  // An empty folder offers both, and the design makes creating a subfolder the
  // primary of the two. With no subfolder to offer, uploading is the only thing
  // the header asks for and takes that weight itself.
  // Creating a subfolder answers to the same permission as uploading into one,
  // and the API refuses it without that permission — so offering it to a viewer
  // is a button whose only outcome is an error toast.
  const showNewFolder = (hasSubfolders || isEmpty) && canNestDeeper && canUpload;

  return (
    // The gap belongs to the content, not to the container: an empty folder has
    // nothing for the header to be separated from, and a gap there pushes the
    // one thing on screen off the centre of the space it is supposed to fill.
    //
    // The card surface itself lives one level up, outside the crossfade, so the
    // pane does not fade in and out as a whole while its contents change.
    <div className="flex min-h-0 flex-1 flex-col">
      <FolderPaneHeader
        projectId={projectId}
        folder={folder}
        showApprovalProgress={hasFiles && !hasSubfolders}
        onDeleted={() => void navigate(routeAfterDelete(), { replace: true })}
        actions={
          // Keyed on the folder, so the actions arrive with the rest of the
          // pane rather than sitting still while everything around them moves.
          // A fade rather than a rise: nothing else on the pane travels, so a
          // moving header read as its own event instead of part of the page.
          //
          // The key is also what ends the flicker. Upload is dark when it stands
          // alone and outline beside New folder; React was reusing the one
          // button and transitioning its colour, so a discrete change played as
          // a crossfade through grey. A new element has nothing to transition
          // from.
          <PageTransition variant="fade" key={folder.id} className="flex items-center gap-xs">
            {/* Sits with the folder's name rather than in the asset toolbar:
                uploading is what you do to this folder, and the toolbar below
                narrows what is already in it. Offered for a folder that holds
                assets and for one that holds nothing yet — never for a folder of
                folders, which cannot take them. */}
            {!hasSubfolders && canUpload && (
              <ActionButton
                size="lg"
                variant={showNewFolder ? 'outline' : 'default'}
                onClick={() => uploadInputRef.current?.click()}
              >
                <UploadIcon className="size-4" />
                {t('projectDetail.folderPane.uploadAssets')}
              </ActionButton>
            )}
            {showNewFolder && (
              <ActionButton size="lg" onClick={() => props.onCreateSubfolder(folder.id)}>
                <FolderPlusIcon className="size-4" />
                {t('projectDetail.folderPane.newFolder')}
              </ActionButton>
            )}
          </PageTransition>
        }
      />

      {hasSubfolders ? (
        <div className="mt-section flex min-h-0 flex-1 flex-col gap-section">
          <SubfolderGrid key={folder.id} projectId={projectId} parentId={folder.id} />
        </div>
      ) : hasFiles ? (
        // `gap-section` because the section renders its toolbar and its grid as
        // siblings: they used to be direct children of the pane and took its
        // gap, so wrapping them without one closed the space between the two.
        <div className="mt-section flex min-h-0 flex-1 flex-col gap-section">
          <AssetsSection
            key={folder.id}
            projectId={projectId}
            folderId={folder.id}
            uploadInputRef={uploadInputRef}
          />
        </div>
      ) : (
        <EmptyFolderPage
          projectId={projectId}
          folderId={folder.id}
          uploadInputRef={uploadInputRef}
        />
      )}
    </div>
  );
}
